import React from 'react';
import * as Location from './fields/Location';
import * as Dates from './fields/Dates';
import * as Occupancy from './fields/Occupancy';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { SubmitButton } from './components/SubmitButton';
import { FormFieldsWrapper } from './components/FormFieldsWrapper';
import { StaysSearchTerms } from '@apps/stays/types';
import { Box, Divider, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { convertToDateRangeFromDateParts } from '@shared/utils/dateHelpers';
import { useTranslation } from '@shared/core';
import { useAccommodationsConsoleConfig } from '@apps/accommodations/AccommodationsConsoleConfigContext';

interface Props {
  onSubmit: (value: StaysSearchTerms) => void;
  initialValue?: Partial<StaysSearchTerms>;
}

export const LocationDatesOccupants = (props: Props) => {
  const today = new Date();
  const threeDaysFromNow = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
  const isMobileOrTablet = useIsMobileOrTablet();
  const {
    topBar: { searchForm },
  } = useAccommodationsConsoleConfig();
  const formHeight = searchForm.dateRangeEnabled || searchForm.occupancySelectorEnabled ? 'fit-content' : '100%';
  const showDatesAndOccupancies = searchForm.dateRangeEnabled || searchForm.occupancySelectorEnabled;

  const { t } = useTranslation('stays');
  const datesOccupantsTrans = t('datesOccupants');
  const validation = {
    childAgeInvalid: datesOccupantsTrans.provideChildAge(),
    childAgeRequired: datesOccupantsTrans.provideChildAge(),
    childAgeMin: datesOccupantsTrans.validation.childAgeMin(),
    childAgeMax: datesOccupantsTrans.validation.childAgeMax(),
  };

  const schema = Yup.object({
    location: Location.schema,
    dates: Dates.schema,
    occupancy: Occupancy.createSchema(validation),
  });
  return (
    <Formik
      initialValues={{
        location: {
          displayName: '',
          latitude: 0,
          longitude: 0,
        },
        // what I would love to do is use 'satisfies' the type of Dates.initialValue
        dates: convertToDateRangeFromDateParts(props.initialValue?.startDate, props.initialValue?.endDate),
        // TODO: fix childrenAges type
        occupancy: props.initialValue?.rooms || Occupancy.initialValue,
      }}
      onSubmit={value => {
        props.onSubmit({
          destination: value.location.displayName,
          lat: value.location.latitude,
          lng: value.location.longitude,
          startDate: {
            year: value.dates.startDate.getFullYear(),
            month: value.dates.startDate.getMonth() + 1,
            day: value.dates.startDate.getDate(),
          },
          endDate: {
            year: value.dates.endDate.getFullYear(),
            month: value.dates.endDate.getMonth() + 1,
            day: value.dates.endDate.getDate(),
          },
          rooms: value.occupancy.map(room => ({
            adults: room.adults,
            childrenAges: room.childrenAges,
          })),
        });
      }}
      validationSchema={schema}
    >
      <Form
        name="search-fields"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: formHeight }}
      >
        {/* TODO: think about how to make this easier to do across different forms, maybe custom provider */}
        {/* Trick to reset the form after async initial values come is */}
        <ResetFormOnNewInitialValue initialValue={props.initialValue} />
        {isMobileOrTablet ? (
          <Flex
            width={'100%'}
            flexDirection={'column'}
            height={formHeight}
            justifyContent={!showDatesAndOccupancies ? 'space-between' : 'flex-start'}
          >
            <Location.Fields fieldsId={'location'} />
            {showDatesAndOccupancies ? (
              <>
                {searchForm.dateRangeEnabled && (
                  <Dates.Fields
                    fieldsId={'dates'}
                    defaultStartDate={today}
                    defaultEndDate={threeDaysFromNow}
                  />
                )}
                {searchForm.occupancySelectorEnabled && <Occupancy.Fields fieldsId={'occupancy'} />}
              </>
            ) : null}
            <SubmitButton />
          </Flex>
        ) : (
          <Flex
            alignItems="center"
            gap={3}
          >
            <FormFieldsWrapper>
              <Box
                gridArea={'location'}
                height={pxToRem(24)}
                minWidth={pxToRem(260)}
              >
                <Location.Fields fieldsId={'location'} />
              </Box>
              {showDatesAndOccupancies ? (
                <>
                  {searchForm.dateRangeEnabled && (
                    <>
                      <Divider
                        orientation="vertical"
                        height={pxToRem(24)}
                      />
                      <Box
                        gridArea={'dates'}
                        height={pxToRem(24)}
                      >
                        <Dates.Fields
                          fieldsId={'dates'}
                          defaultStartDate={today}
                          defaultEndDate={threeDaysFromNow}
                        />
                      </Box>
                      <Divider
                        orientation="vertical"
                        height={pxToRem(24)}
                      />
                    </>
                  )}
                  {searchForm.occupancySelectorEnabled && (
                    <Box gridArea={'occupancy'}>
                      <Occupancy.Fields fieldsId={'occupancy'} />
                    </Box>
                  )}
                </>
              ) : null}
            </FormFieldsWrapper>
            <Box gridArea={'submit'}>
              <SubmitButton />
            </Box>
          </Flex>
        )}
      </Form>
    </Formik>
  );
};

const ResetFormOnNewInitialValue = (props: Pick<Props, 'initialValue'>) => {
  const formik = useFormikContext();
  React.useEffect(() => {
    formik.resetForm({
      values: {
        location: {
          displayName: props.initialValue?.destination,
          latitude: props.initialValue?.lat,
          longitude: props.initialValue?.lng,
        },
        dates: convertToDateRangeFromDateParts(props.initialValue?.startDate, props.initialValue?.endDate),
        occupancy: props.initialValue?.rooms || Occupancy.initialValue,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValue]);
  return null;
};
